/**
 * Доля хвоста публичного рендерера: вид `likert_scale` (шкала согласия).
 *
 * WEB-2920, заход 4: перенесено из `PublicBlockRendererExtended.tsx` слово в
 * слово. Метка `case 'likert_scale'` остаётся в фасаде; ветка вида — функция
 * отрисовки `renderLikertScale(ctx)`. Замыкания фасада приходят объектом-контекстом
 * (`ExtendedRenderContext`); ответ посетителя ветка читает из `values` и пишет
 * через `setValue` — это `useFormValues()` фасада: хук стоит до `switch`, в
 * функции отрисовки его звать нельзя (рисуется одна ветка за рендер). Эту
 * долю импортирует только фасад: она едет в ленивом куске `extended`, а не в
 * первом экране (самопроверка обхода).
 */
import React from 'react';
import { asRichField, richFieldToPlainText } from '@/types/rich-text';
import {
  blockTextRoles,
  mutedTextRoleStyle,
  scaledTextRoleStyle,
} from '../semanticTextRoles';
import { getPublicQuizStrings } from '@/utils/publicQuizStrings';
import type { ExtendedRenderContext } from '../PublicBlockRendererExtended';
import { LIKERT_DEFAULT_EMOJIS } from '../likertDefaultEmojis';

export function renderLikertScale(
  ctx: ExtendedRenderContext,
): React.ReactElement {
  const { block, varCtx, interp, richText, hasText, values, setValue } = ctx;
  // Атом 8 (референс 12min): ряд эмодзи-кружков с подписями концов шкалы.
  // Отдельный тип вопроса, а не оформление single_choice — здесь равные
  // кружки в ряд, подписи по краям и цифры под ними.
  // WEB-1625 (решение владельца 24.08, вариант Б): подписи делений
  // (likertLabels) и эмодзи (likertEmojis) в схеме бэка — простые строки
  // (z.array(z.string())), в реестр rich-полей 1465-м намеренно НЕ
  // внесены: оформление в подписях делений продукту не нужно. Значит и
  // печатаем их простым текстом, а не через richText():
  //   - разметка модели («**вот так**») показывается ДОСЛОВНО — честное
  //     поведение plain-поля, как у label варианта;
  //   - если в поле всё-таки затечёт массив спанов (плохие данные), берём
  //     его голый текст: без жирного/цвета, без «[object Object]» и без
  //     падения всей публичной страницы (React роняет рендер на объекте
  //     в детях — раньше именно так падал ряд эмодзи).
  // Соседние content/likertMinLabel/likertMaxLabel остаются rich по 1465.
  const likertPlain = (value: string | undefined): string => {
    const field = asRichField(value);
    if (field == null) return '';
    if (typeof field === 'string') return field;
    if (Array.isArray(field)) return richFieldToPlainText(field);
    return '';
  };
  const points = Math.min(7, Math.max(3, block.likertPoints ?? 5));
  const defaultEmojis = LIKERT_DEFAULT_EMOJIS;
  const emojis =
    Array.isArray(block.likertEmojis) &&
    block.likertEmojis.length >= points
      ? block.likertEmojis
      : defaultEmojis;
  const showNumbers = block.likertShowNumbers !== false;
  const current = String(values[block.id] ?? '');
  // WEB-926: подписи всех делений (12min). Массив не задан или пуст —
  // ведём себя как раньше: подписи только по краям шкалы.
  const pointLabels = Array.isArray(block.likertLabels)
    ? block.likertLabels
    : undefined;
  const hasPointLabels = (pointLabels ?? []).some(
    (label) => likertPlain(label) !== '',
  );
  // WEB-1692 юнит 8: утверждение — роль Title; подписи делений, цифры и
  // подписи краёв — одна роль Secondary. Эмодзи в роль не входят: их
  // размер считается от числа делений, и состояние «выбрано» несут
  // яркость с масштабом (WEB-960) — накладка цвета там ничего не значит.
  const likertRoles = blockTextRoles(block, 'likert_scale');
  const likertSecondary = likertRoles.secondary;
  // Края сегодня крупнее подписей делений (13 против 12) — пропорция
  // сохраняется каноном варианта А, а не вторым числом от автора.
  const likertEdgeRole = scaledTextRoleStyle(likertSecondary, 13 / 12);
  const likertMuted = mutedTextRoleStyle(likertSecondary);

  return (
    <div style={{ marginBottom: 16 }}>
      {hasText(block.content) && (
        <p
          style={{
            fontWeight: 600,
            marginBottom: 12,
            textAlign: 'center',
            ...(likertRoles.title.color
              ? { color: likertRoles.title.color }
              : {}),
            ...(likertRoles.title.fontSize !== undefined
              ? { fontSize: likertRoles.title.fontSize }
              : {}),
          }}
        >
          {richText(block.content)}
        </p>
      )}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 8,
        }}
      >
        {Array.from({ length: points }).map((_, i) => {
          const value = String(i + 1);
          const selected = current === value;
          return (
            <div
              key={value}
              data-testid={`likert-point-${value}`}
              data-selected={selected ? 'true' : 'false'}
              onClick={() => setValue(block.id, value)}
              role="button"
              aria-label={getPublicQuizStrings(
                varCtx?.system?.locale,
              ).ratingAria(Number(value), points)}
              style={{
                flex: 1,
                // WEB-960: без minWidth: 0 flex-элемент не ужимается ниже
                // своего содержимого, и семь делений вылезают за ширину
                // превью редактора (≈268px, шрам WEB-958).
                minWidth: '0px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 6,
                cursor: 'pointer',
              }}
            >
              <span
                data-testid={`likert-emoji-${value}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  // WEB-960: смайл стоит сам по себе. Круг с подложкой и
                  // рамкой читался как коробочка вокруг эмодзи (особенно на
                  // тёмной теме), а одинаково яркие смайлы не давали понять,
                  // что выбрано. Состояние теперь несут яркость и размер —
                  // как у 12min.
                  background: 'none',
                  border: 'none',
                  // Семь смайлов должны влезать в узкую колонку, пять могут
                  // быть крупнее: размер считается от числа делений.
                  fontSize: points >= 6 ? 26 : 30,
                  lineHeight: 1,
                  padding: 2,
                  opacity: selected ? 1 : 0.55,
                  filter: selected ? 'none' : 'grayscale(0.5)',
                  transform: selected ? 'scale(1.15)' : 'scale(1)',
                  // Свечение вместо кольца: рамку у выбранного глаз читал бы
                  // как ту же коробочку, от которой ушли.
                  textShadow: selected
                    ? '0 0 18px color-mix(in srgb, var(--color-primary, #4094f7) 55%, transparent)'
                    : 'none',
                  transition:
                    'opacity var(--transition-ms, 160ms) ease, transform var(--transition-ms, 160ms) ease, filter var(--transition-ms, 160ms) ease',
                }}
              >
                {likertPlain(emojis[i])}
              </span>
              {showNumbers && (
                <span
                  data-testid={`likert-number-${value}`}
                  style={{
                    fontSize: likertSecondary.fontSize ?? 12,
                    // WEB-960: у выбранного номер ещё и плотнее — цвет один
                    // на светлой теме мог теряться.
                    fontWeight: selected ? 600 : 400,
                    color:
                      likertSecondary.color ??
                      (selected
                        ? 'var(--color-primary, #4094f7)'
                        : 'var(--color-text-secondary, #8c8c8c)'),
                    // Без авторского цвета разницу состояний по-прежнему
                    // несут переменные темы — приглушать нечем и нечего.
                    ...(selected ? {} : likertMuted),
                  }}
                >
                  {value}
                </span>
              )}
              {likertPlain(pointLabels?.[i]) !== '' && (
                <span
                  data-testid={`likert-label-${value}`}
                  style={{
                    fontSize: likertSecondary.fontSize ?? 12,
                    lineHeight: 1.25,
                    textAlign: 'center',
                    color:
                      likertSecondary.color ??
                      (selected
                        ? 'var(--color-text-primary, inherit)'
                        : 'var(--color-text-secondary, #8c8c8c)'),
                    ...(selected ? {} : likertMuted),
                  }}
                >
                  {/* Простой текст: подстановку переменных сохраняем
                    (её делал и richText для plain-строк), оформление —
                    нет. WEB-1625, вариант Б. */}
                  {interp(likertPlain(pointLabels?.[i]))}
                </span>
              )}
            </div>
          );
        })}
      </div>
      {/* Подписи краёв печатаются, только пока нет подписей делений: иначе
        один и тот же текст встал бы дважды. */}
      {!hasPointLabels &&
        (hasText(block.likertMinLabel) ||
          hasText(block.likertMaxLabel)) && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: 8,
              fontSize: likertEdgeRole.fontSize ?? 13,
              color:
                likertEdgeRole.color ??
                'var(--color-text-secondary, #8c8c8c)',
            }}
          >
            <span>{richText(block.likertMinLabel)}</span>
            <span>{richText(block.likertMaxLabel)}</span>
          </div>
        )}
    </div>
  );
}
